import { DASHBOARD_TAGS } from '../../component/governanceV2/subcomponents/util/constants';
import {
    get,
    patch,
} from '../utility/Requests';

interface Tag {
    name: string;
    value: string;
}

interface DeploymentFolder {
    key: string;
    displayName?: string;
    fullyQualifiedName?: string;
}

/** From `definition.codedAppMetadata`. On the list projection only — the by-id read omits it. */
interface CodedAppMetadata {
    packageName?: string;
    packageVersion?: string;
}

export interface CodedDashboard {
    id: string;
    title: string;
    deploymentTitle: string;
    /** Platform ordinal (1, 2, 3…), not a semver. Never render it as a version. */
    deployVersion: number;
    deploymentDescription: string;
    deploymentFolder: DeploymentFolder;
    systemName: string;
    routingName: string;
    tags: string[];
    /** Absent on dashboards that were not deployed from a template package. */
    packageName?: string;
    /** Absent means unknown, never "up to date". */
    packageVersion?: string;
}

interface DeployedAppItem {
    id: string;
    title: string;
    deploymentTitle: string;
    deployVersion: number;
    deploymentDescription: string;
    deploymentFolder: DeploymentFolder;
    systemName: string;
    routingName?: string;
    tags: Tag[];
    definition?: { codedAppMetadata?: CodedAppMetadata };
}

const DEPLOYED_APPS_URL = '/apps_/default/api/v1/default/models/deployed/apps';
const EDIT_DASHBOARD_BFF_URL = '/api/orchestrator/apps';

interface DeployedAppsResponse {
    value?: DeployedAppItem[];
    /** True total, independent of the page. */
    '@odata.count'?: number;
}

/** Rows without a `routingName` are dropped — without one a dashboard cannot be opened. */
function toCodedDashboards(items: DeployedAppItem[]): CodedDashboard[] {
    return items
        .filter((item): item is DeployedAppItem & { routingName: string } =>
            typeof item.routingName === 'string')
        .map(item => ({
            id: item.id,
            title: item.title,
            deploymentTitle: item.deploymentTitle,
            deployVersion: item.deployVersion,
            deploymentDescription: item.deploymentDescription,
            deploymentFolder: item.deploymentFolder,
            systemName: item.systemName,
            routingName: item.routingName,
            tags: (item.tags ?? []).map(tag => tag.value),
            packageName: item.definition?.codedAppMetadata?.packageName,
            packageVersion: item.definition?.codedAppMetadata?.packageVersion,
        }));
}

interface CodedDashboardPage {
    dashboards: CodedDashboard[];
    /** `@odata.count` — the total matching the filter, independent of this page. */
    total: number;
}

interface CodedDashboardPageQuery {
    top: number;
    skip: number;
    /** Matches `deploymentTitleLowerCase` server-side. Title only. */
    searchText?: string;
    /** Narrows to pinned rows. The platform ANDs multiple tags, so this is `governance` *and* `dashboard`. */
    pinnedOnly?: boolean;
    /**
     * Scopes to one folder, server-side. Omitted means every folder the caller may view, which is what
     * the nav, picker and library row all want — only a lookup for a known deployment sets this.
     */
    folderKey?: string;
}

function fetchDeployedApps(
    tenantId: string,
    query: CodedDashboardPageQuery,
): Promise<DeployedAppsResponse> {
    return get<DeployedAppsResponse>(
        DEPLOYED_APPS_URL,
        {
            stripPortalPath: true,
            headers: {
                'X-UiPath-Internal-TenantId': tenantId,
                // A header, not a query param — the platform reads folder scope off the request context.
                ...query.folderKey ? { 'x-uipath-folderkey': query.folderKey } : {},
            },
            urlParams: {
                tags: query.pinnedOnly
                    ? `${DASHBOARD_TAGS.GOVERNANCE},${DASHBOARD_TAGS.PINNED}`
                    : DASHBOARD_TAGS.GOVERNANCE,
                top: query.top,
                skip: query.skip,
                ...query.searchText ? { searchText: query.searchText } : {},
            },
        },
    );
}

export async function fetchCodedDashboardsPage(
    tenantId: string,
    query: CodedDashboardPageQuery,
): Promise<CodedDashboardPage> {
    const data = await fetchDeployedApps(tenantId, query);

    return {
        dashboards: toCodedDashboards(data?.value ?? []),
        // Absent count means no more than what arrived.
        total: typeof data?.['@odata.count'] === 'number' ? data['@odata.count'] : (data?.value ?? []).length,
    };
}

export async function fetchCodedDashboardRoutingName(
    tenantId: string,
    folderKey: string,
    id: string,
): Promise<string | null> {
    const data = await get<{ routingName?: string }>(
        `${DEPLOYED_APPS_URL}/${id}`,
        {
            stripPortalPath: true,
            headers: {
                'X-UiPath-Internal-TenantId': tenantId,
                'x-uipath-folderkey': folderKey,
            },
        },
    );

    // A blank routing name is as unusable as a missing one, so both collapse to null.
    return data?.routingName ? data.routingName : null;
}

/**
 * Enough to cover the governance dashboards a tenant realistically deploys. Not a full scan: this
 * only backs links predating the folder key on the route, and a miss falls through to the not-found
 * screen rather than paging the whole listing.
 */
const CROSS_FOLDER_LOOKUP_PAGE_SIZE = 200;

/**
 * Routing name for a deployment whose folder is unknown — a link made before the route carried one.
 *
 * The by-id read cannot answer this: it resolves the routing name from a row keyed by folder, so
 * without one it returns the deployment with no routing name at all. The listing, given no folder
 * key, spans every folder the caller may view and resolves routing names by deployment id, so the
 * row it returns is already the answer.
 */
export async function findCodedDashboardAcrossFolders(
    tenantId: string,
    id: string,
): Promise<CodedDashboard | null> {
    const { dashboards } = await fetchCodedDashboardsPage(tenantId, {
        top: CROSS_FOLDER_LOOKUP_PAGE_SIZE,
        skip: 0,
    });

    return dashboards.find(row => row.id === id) ?? null;
}

export interface EditCodedDashboardPayload {
    version: number;
    description: string;
    title: string;
    tags: string[];
}

export async function editCodedDashboard(
    tenantId: string,
    folderKey: string,
    id: string,
    payload: EditCodedDashboardPayload,
): Promise<void> {
    await patch<void>(
        `${EDIT_DASHBOARD_BFF_URL}/${id}`,
        {
            headers: {
                extendRequestHeaders: 'true',
                'x-uipath-internal-tenantid': tenantId,
                'x-uipath-folderkey': folderKey,
            },
            body: payload,
        },
    );
}
